import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { useSocket } from '../context/SocketContext';
import { pageVariants, staggerContainer, staggerItem } from '../theme/space';
import { IconBarChart, IconTrendUp, IconActivity, IconBroadcast } from '../components/Icons';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n) { return (n || 0).toLocaleString(); }
function pct(a, b) { return b > 0 ? Math.round((a / b) * 100) : 0; }

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function duration(a, b) {
  if (!a || !b) return '—';
  const ms = new Date(b) - new Date(a);
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

const STATUS_STYLE = {
  completed:   { bg: 'rgba(52,211,153,0.12)',  color: '#34d399' },
  cancelled:   { bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
  'in-progress': { bg: 'rgba(56,189,248,0.12)',  color: '#38bdf8' },
  failed:      { bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
  pending:     { bg: 'rgba(251,191,36,0.12)',  color: '#fbbf24' },
};

// ─── Mini sparkline bar chart ─────────────────────────────────────────────────
function SparkBars({ data }) {
  const values = Object.values(data);
  const max = Math.max(...values, 1);
  const last14 = values.slice(-14);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 40 }}>
      {last14.map((v, i) => (
        <div
          key={i}
          title={`${v} sent`}
          style={{
            flex: 1,
            height: `${Math.max((v / max) * 100, v > 0 ? 8 : 3)}%`,
            minHeight: 2,
            borderRadius: 2,
            background: v > 0
              ? 'linear-gradient(180deg, #818cf8, #6366f1)'
              : 'rgba(99,102,241,0.12)',
            transition: 'height 0.3s ease',
          }}
        />
      ))}
    </div>
  );
}

// ─── Success rate ring ─────────────────────────────────────────────────────────
function RateRing({ rate, size = 64, strokeWidth = 5 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (rate / 100) * circ;
  const color = rate >= 80 ? '#34d399' : rate >= 50 ? '#fbbf24' : '#f87171';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(99,102,241,0.12)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${fill} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  );
}

// ─── Active Broadcast Live Panel ──────────────────────────────────────────────
function ActivePanel({ broadcast, logs, onStop, stopping }) {
  const logEndRef = useRef(null);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);

  const pctDone = broadcast.total > 0 ? (broadcast.sent / broadcast.total) * 100 : 0;
  const ss = STATUS_STYLE[broadcast.status] || STATUS_STYLE.pending;

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#38bdf8', boxShadow: '0 0 8px #38bdf8', animation: 'pulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0' }}>Live Broadcast</span>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, ...ss }}>{broadcast.status.toUpperCase()}</span>
        </div>
        {(broadcast.status === 'sending' || broadcast.status === 'starting') && (
          <button onClick={onStop} disabled={stopping} style={s.stopBtn}>
            {stopping ? 'Stopping…' : 'Stop Broadcast'}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
        <div><div style={s.metaLabel}>Sent</div><div style={s.metaVal}>{fmt(broadcast.sent)}</div></div>
        <div><div style={s.metaLabel}>Total</div><div style={s.metaVal}>{fmt(broadcast.total)}</div></div>
        <div><div style={s.metaLabel}>Progress</div><div style={s.metaVal}>{Math.round(pctDone)}%</div></div>
      </div>

      <div style={s.progressBg}>
        <div style={{ ...s.progressFill, width: `${pctDone}%` }} />
      </div>

      <div style={s.terminal}>
        {logs.map((L, i) => (
          <div key={i} style={{ color: L.type === 'error' ? '#ef4444' : L.type === 'success' ? '#22c55e' : '#94a3b8', marginBottom: 1 }}>
            [{new Date().toLocaleTimeString()}] {L.type === 'success' ? '✓' : L.type === 'error' ? '✗' : '·'} {L.number}{L.reason ? ` — ${L.reason}` : ''}
          </div>
        ))}
        <div ref={logEndRef} />
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BroadcastAnalytics() {
  const { socket } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // selected broadcast _id for detail
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stopping, setStopping] = useState(false);

  useEffect(() => { loadAnalytics(); }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (update) => {
      setActiveBroadcast({ id: update.broadcastId, sent: update.sent, total: update.total, status: update.status });
      if (update.log) setLogs(prev => [...prev, update.log]);
      if (update.status === 'completed' || update.status === 'cancelled') {
        setStopping(false);
        loadAnalytics(); // refresh stats
      }
    };
    socket.on('broadcast_update', handler);
    return () => socket.off('broadcast_update', handler);
  }, [socket]);

  async function loadAnalytics() {
    try {
      const { data: d } = await api.get('/api/broadcast/analytics');
      setData(d);
      // If there's an in-progress broadcast, pre-set it
      const live = d.detail.find(b => b.status === 'in-progress');
      if (live && !activeBroadcast) {
        setActiveBroadcast({ id: live._id, sent: live.successfulSends, total: live.totalContacts, status: 'sending' });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleStop() {
    if (!activeBroadcast?.id) return;
    setStopping(true);
    try {
      await api.post(`/api/broadcast/${activeBroadcast.id}/stop`);
      setLogs(prev => [...prev, { type: 'info', number: 'System', reason: 'Stop signal sent.' }]);
    } catch (err) {
      setStopping(false);
      alert(err.response?.data?.error || 'Failed to stop broadcast');
    }
  }

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#818cf8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!data) return <div style={s.page}><p style={{ color: '#64748b' }}>Failed to load analytics.</p></div>;

  const { summary, statusBreakdown, dailyVolume, detail } = data;
  const statusColors = { completed: '#34d399', cancelled: '#f87171', 'in-progress': '#38bdf8', failed: '#f87171', pending: '#fbbf24' };
  const selectedBroadcast = selected ? detail.find(b => b._id === selected) : null;

  const statCards = [
    { label: 'Total Broadcasts', value: fmt(summary.totalBroadcasts), icon: IconBroadcast, glow: '#818cf8', color: '#818cf8' },
    { label: 'Messages Sent', value: fmt(summary.totalSent), icon: IconTrendUp, glow: '#34d399', color: '#34d399' },
    { label: 'Failed', value: fmt(summary.totalFailed), icon: IconActivity, glow: '#f87171', color: '#f87171' },
    { label: 'Success Rate', value: `${summary.overallRate}%`, icon: IconBarChart, glow: '#fbbf24', color: '#fbbf24' },
  ];

  return (
    <motion.div style={s.page} variants={pageVariants} initial="initial" animate="animate">
      <div style={s.header}>
        <h1 style={s.title}>Broadcast Analytics</h1>
        <p style={s.sub}>Real-time performance insights across all your campaigns</p>
      </div>

      {/* Live Active Broadcast */}
      {activeBroadcast && (activeBroadcast.status === 'sending' || activeBroadcast.status === 'starting' || activeBroadcast.status === 'in-progress') && (
        <ActivePanel broadcast={activeBroadcast} logs={logs} onStop={handleStop} stopping={stopping} />
      )}

      {/* Summary Cards */}
      <motion.div style={s.statGrid} variants={staggerContainer} initial="initial" animate="animate">
        {statCards.map((c) => (
          <motion.div key={c.label} style={{ ...s.statCard, '--glow': c.glow }} variants={staggerItem}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={s.statLabel}>{c.label}</div>
                <div style={{ ...s.statValue, color: c.color }}>{c.value}</div>
              </div>
              <div style={{ ...s.iconWrap, background: `${c.glow}1a`, border: `1px solid ${c.glow}33` }}>
                <c.icon size={16} color={c.color} />
              </div>
            </div>
            <div style={{ ...s.statGlow, background: c.glow }} />
          </motion.div>
        ))}
      </motion.div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {/* Daily Volume Chart */}
        <div style={{ ...s.card, flex: '2 1 340px' }}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Daily Send Volume</span>
            <span style={s.cardSub}>Last 14 days</span>
          </div>
          {Object.values(dailyVolume).every(v => v === 0)
            ? <p style={{ color: '#475569', fontSize: 13, marginTop: 12 }}>No messages sent in the last 30 days.</p>
            : (
              <>
                <SparkBars data={dailyVolume} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={s.axisLabel}>{Object.keys(dailyVolume).slice(-14)[0]?.slice(5)}</span>
                  <span style={s.axisLabel}>{Object.keys(dailyVolume).at(-1)?.slice(5)}</span>
                </div>
              </>
            )
          }
        </div>

        {/* Status Breakdown */}
        <div style={{ ...s.card, flex: '1 1 200px' }}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Status Breakdown</span>
          </div>
          {Object.entries(statusBreakdown).length === 0
            ? <p style={{ color: '#475569', fontSize: 13, marginTop: 12 }}>No broadcasts yet.</p>
            : Object.entries(statusBreakdown).map(([status, count]) => (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[status] || '#818cf8', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: '#94a3b8', flex: 1, textTransform: 'capitalize' }}>{status.replace('-', ' ')}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{count}</span>
                <div style={{ width: 60, height: 4, borderRadius: 2, background: 'rgba(99,102,241,0.1)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct(count, summary.totalBroadcasts)}%`, background: statusColors[status] || '#818cf8', borderRadius: 2 }} />
                </div>
              </div>
            ))
          }
        </div>

        {/* Overall rate ring */}
        <div style={{ ...s.card, flex: '0 0 160px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={s.cardTitle}>Overall Rate</span>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RateRing rate={summary.overallRate} size={88} strokeWidth={7} />
            <span style={{ position: 'absolute', fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{summary.overallRate}%</span>
          </div>
          <span style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>
            {fmt(summary.totalSent)} of {fmt(summary.totalContacts)} delivered
          </span>
        </div>
      </div>

      {/* Broadcast Table + Detail */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {/* Table */}
        <div style={{ ...s.card, flex: '2 1 400px', minWidth: 0 }}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>All Broadcasts</span>
            <span style={s.cardSub}>{detail.length} total</span>
          </div>
          {detail.length === 0
            ? <p style={{ color: '#475569', fontSize: 13 }}>No broadcasts found.</p>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {['Name', 'Status', 'Sent / Total', 'Success', 'Date'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.map(b => {
                      const ss = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
                      const isActive = selected === b._id;
                      return (
                        <tr
                          key={b._id}
                          onClick={() => setSelected(isActive ? null : b._id)}
                          style={{ cursor: 'pointer', background: isActive ? 'rgba(99,102,241,0.08)' : 'transparent', transition: 'background 0.15s' }}
                        >
                          <td style={s.td}>
                            <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 13 }}>{b.name}</div>
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{b.templateName}</div>
                          </td>
                          <td style={s.td}>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5, ...ss }}>
                              {b.status.replace('-', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td style={s.td}>
                            <span style={{ color: '#34d399', fontWeight: 600 }}>{fmt(b.successfulSends)}</span>
                            <span style={{ color: '#475569' }}> / {fmt(b.totalContacts)}</span>
                          </td>
                          <td style={s.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div style={{ flex: 1, height: 5, background: 'rgba(99,102,241,0.1)', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
                                <div style={{
                                  height: '100%',
                                  width: `${b.successRate}%`,
                                  background: b.successRate >= 80 ? '#34d399' : b.successRate >= 50 ? '#fbbf24' : '#f87171',
                                  borderRadius: 3,
                                  transition: 'width 0.4s ease',
                                }} />
                              </div>
                              <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{b.successRate}%</span>
                            </div>
                          </td>
                          <td style={{ ...s.td, fontSize: 11, color: '#64748b' }}>{fmtDate(b.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>

        {/* Detail Panel */}
        {selectedBroadcast && (
          <motion.div
            key={selectedBroadcast._id}
            style={{ ...s.card, flex: '1 1 240px' }}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <span style={s.cardTitle}>{selectedBroadcast.name}</span>
              <button onClick={() => setSelected(null)} style={s.closeBtn}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Status', value: selectedBroadcast.status.replace('-', ' '), color: STATUS_STYLE[selectedBroadcast.status]?.color },
                { label: 'Template', value: selectedBroadcast.templateName },
                { label: 'Total Contacts', value: fmt(selectedBroadcast.totalContacts) },
                { label: 'Delivered', value: fmt(selectedBroadcast.successfulSends), color: '#34d399' },
                { label: 'Failed', value: fmt(selectedBroadcast.failedSends), color: selectedBroadcast.failedSends > 0 ? '#f87171' : undefined },
                { label: 'Success Rate', value: `${selectedBroadcast.successRate}%`, color: selectedBroadcast.successRate >= 80 ? '#34d399' : '#fbbf24' },
              ].map(item => (
                <div key={item.label} style={s.detailCell}>
                  <div style={s.detailLabel}>{item.label}</div>
                  <div style={{ ...s.detailVal, color: item.color || '#e2e8f0' }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid rgba(99,102,241,0.1)', paddingTop: 12 }}>
              <div style={s.detailLabel}>Started</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{fmtDate(selectedBroadcast.createdAt)}</div>
              <div style={s.detailLabel}>Completed</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{fmtDate(selectedBroadcast.completedAt)}</div>
              <div style={s.detailLabel}>Duration</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{duration(selectedBroadcast.createdAt, selectedBroadcast.completedAt)}</div>
            </div>

            {/* Success rate ring in detail */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 16, gap: 12 }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RateRing rate={selectedBroadcast.successRate} size={72} strokeWidth={6} />
                <span style={{ position: 'absolute', fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>{selectedBroadcast.successRate}%</span>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#34d399', fontWeight: 600 }}>{fmt(selectedBroadcast.successfulSends)} delivered</div>
                {selectedBroadcast.failedSends > 0 && <div style={{ fontSize: 12, color: '#f87171', fontWeight: 600, marginTop: 2 }}>{fmt(selectedBroadcast.failedSends)} failed</div>}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto', background: 'transparent', minHeight: '100vh' },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginBottom: 4, letterSpacing: -0.3 },
  sub: { color: '#64748b', fontSize: 13 },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 },
  statCard: {
    background: 'rgba(6, 12, 28, 0.9)',
    border: '1px solid rgba(99,102,241,0.15)',
    borderRadius: 12,
    padding: '18px 20px',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    position: 'relative',
    overflow: 'hidden',
  },
  statLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 },
  statValue: { fontSize: 26, fontWeight: 700, letterSpacing: -0.5 },
  statGlow: { position: 'absolute', bottom: -20, right: -20, width: 80, height: 80, borderRadius: '50%', opacity: 0.07, filter: 'blur(24px)' },
  iconWrap: { width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },

  card: {
    background: 'rgba(6, 12, 28, 0.9)',
    border: '1px solid rgba(99,102,241,0.15)',
    borderRadius: 12,
    padding: 20,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    marginBottom: 20,
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#e2e8f0' },
  cardSub: { fontSize: 11, color: '#475569' },
  axisLabel: { fontSize: 10, color: '#475569' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' },
  th: { borderBottom: '1px solid rgba(99,102,241,0.12)', padding: '8px 12px', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 },
  td: { borderBottom: '1px solid rgba(99,102,241,0.06)', padding: '12px', color: '#94a3b8', verticalAlign: 'middle' },

  detailCell: { background: 'rgba(2,5,16,0.5)', borderRadius: 8, padding: '10px 12px', border: '1px solid rgba(99,102,241,0.1)' },
  detailLabel: { fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 },
  detailVal: { fontSize: 14, fontWeight: 700 },

  closeBtn: { background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14, padding: '2px 6px', borderRadius: 4 },

  progressBg: { width: '100%', height: 5, background: 'rgba(2,5,16,0.8)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #6366f1, #818cf8)', transition: 'width 0.3s ease', borderRadius: 3 },
  terminal: { background: 'rgba(0,0,0,0.5)', borderRadius: 8, padding: '10px 12px', height: 160, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11, border: '1px solid rgba(99,102,241,0.12)', backdropFilter: 'blur(4px)' },
  stopBtn: { padding: '6px 14px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: 8, color: '#f87171', fontWeight: 600, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  metaLabel: { fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3 },
  metaVal: { fontSize: 18, fontWeight: 700, color: '#e2e8f0' },
};
